/**
 * Capitalizes the first letter of a string
 * @param {string} text - The input text
 * @returns {string} Text with first letter capitalized
 */
export const capitalizeFirstLetter = (text) => {
  if (!text || typeof text !== 'string' || text.length === 0) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Capitalizes the first letter of each word in a string
 * @param {string} text - The input text
 * @returns {string} Text with each word capitalized
 */
export const capitalizeEachWord = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .split(' ')
    .map((word) =>
      word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word,
    )
    .join(' ');
};

/**
 * Converts text to lowercase
 * @param {string} text - The input text
 * @returns {string} Lowercase text
 */
export const toLowerCase = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.toLowerCase();
};

/**
 * Converts text to uppercase
 * @param {string} text - The input text
 * @returns {string} Uppercase text
 */
export const toUpperCase = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.toUpperCase();
};

/**
 * Trims whitespace from both ends of a string
 * @param {string} text - The input text
 * @returns {string} Trimmed text
 */
export const trim = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.trim();
};

/**
 * Truncates text to a specified length and adds ellipsis if truncated
 * @param {string} text - The input text
 * @param {number} maxLength - Maximum length before truncation
 * @param {string} [ellipsis='...'] - String to append after truncation
 * @returns {string} Truncated text
 */
export const truncate = (text, maxLength, ellipsis = '...') => {
  if (!text || typeof text !== 'string' || text.length <= maxLength)
    return text;
  return text.slice(0, maxLength) + ellipsis;
};

/**
 * Converts text to camelCase (e.g., "hello world" -> "helloWorld")
 * @param {string} text - The input text
 * @returns {string} camelCase text
 */
export const toCamelCase = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase());
};

/**
 * Converts text to kebab-case (e.g., "hello world" -> "hello-world")
 * @param {string} text - The input text
 * @returns {string} kebab-case text
 */
export const toKebabCase = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '');
};

/**
 * Converts text to snake_case (e.g., "hello world" -> "hello_world")
 * @param {string} text - The input text
 * @returns {string} snake_case text
 */
export const toSnakeCase = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '');
};

/**
 * Formats a number with commas (e.g., 1000 -> "1,000")
 * @param {number} num - The input number
 * @returns {string} Formatted number
 */
export const formatNumber = (num) => {
  if (num === undefined || num === null || isNaN(num)) return '';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Strips HTML tags from a string
 * @param {string} html - The HTML string
 * @returns {string} Text without HTML tags
 */
export const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') return html;
  return html.replace(/<[^>]*>/g, '');
};

/**
 * Escapes HTML special characters
 * @param {string} text - The input text
 * @returns {string} HTML escaped text
 */
export const escapeHtml = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

/**
 * Slugifies a string for URLs (e.g., "Hello World!" -> "hello-world")
 * @param {string} text - The input text
 * @returns {string} URL-friendly slug
 */
export const slugify = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Formats text as a sentence (capitalizes first letter, ensures period at end)
 * @param {string} text - The input text
 * @returns {string} Formatted sentence
 */
export const formatSentence = (text) => {
  if (!text || typeof text !== 'string') return text;
  let trimmed = text.trim();
  let capitalized = capitalizeFirstLetter(trimmed);

  // Check if the text already ends with punctuation
  const endsWithPunctuation = /[.!?]$/.test(capitalized);
  if (!endsWithPunctuation) {
    capitalized += '.';
  }

  return capitalized;
};

const textFormatUtils = {
  capitalizeFirstLetter,
  capitalizeEachWord,
  toLowerCase,
  toUpperCase,
  trim,
  truncate,
  toCamelCase,
  toKebabCase,
  toSnakeCase,
  formatNumber,
  stripHtmlTags,
  escapeHtml,
  slugify,
  formatSentence,
};

export default textFormatUtils;
